import React, { useEffect, useState } from "react";
import { Button, Col, DatePicker, Form, Input, Row, Select, TimePicker } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import { ErrorToast, SuccessToast } from "../../../widgets/AppToast";
import { post } from "../../../services/services";
import { ApiURL } from "../../../services/apiConstants";
import VoyageDetails from "../../../components/VoyageDetails";

const { TextArea } = Input;

const ENTRY_TYPES = [
  { label: "Work", value: "work" },
  { label: "Rest", value: "rest" },
  { label: "Break", value: "break" },
  { label: "Meal", value: "meal" },
  { label: "Overtime", value: "overtime" },
];

const calcDuration = (start, end) => {
  if (!start || !end) return null;
  const s = dayjs(`2000-01-01 ${start}`);
  let e = dayjs(`2000-01-01 ${end}`);
  if (e.isBefore(s)) e = e.add(1, "day");
  return parseFloat((e.diff(s, "minute") / 60).toFixed(2));
};

const EditRestHours = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [form] = Form.useForm();
  const [loader, set_loader] = useState(false);
  const [staff_dashboard, set_staff_dashboard] = useState([]);

  useEffect(() => {
    const staffData = JSON.parse(localStorage.getItem("staff_dashboard") || "{}");
    set_staff_dashboard(staffData);
    GET_REST_HOURS_API();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const GET_REST_HOURS_API = () => {
    const FORM_DATA = new FormData();
    FORM_DATA.append("log_id", atob(id));
    try {
      post(ApiURL.view_or_update_rest_hours_st, FORM_DATA).then((res) => {
        if (res?.status === true) {
          const d = res.data || res.rest_hours || {};
          form.setFieldsValue({
            date: d.date ? dayjs(d.date) : null,
            start_time: d.start_time ? dayjs(`2000-01-01 ${d.start_time}`, "YYYY-MM-DD HH:mm") : null,
            end_time: d.end_time ? dayjs(`2000-01-01 ${d.end_time}`, "YYYY-MM-DD HH:mm") : null,
            entry_type: d.entry_type,
            duration: d.duration,
            description: d.description,
          });
        } else {
          ErrorToast("Failed to load entry");
        }
      });
    } catch (error) {
      console.error("Error fetching entry:", error);
    }
  };

  const onValuesChange = (_, all) => {
    const start = all.start_time ? all.start_time.format("HH:mm") : null;
    const end = all.end_time ? all.end_time.format("HH:mm") : null;
    const dur = calcDuration(start, end);
    form.setFieldsValue({ duration: dur !== null ? dur : "" });
  };

  const onFinish = (values) => {
    set_loader(true);
    const FORM_DATA = new FormData();

    FORM_DATA.append("log_id", atob(id));
    FORM_DATA.append("date", values.date ? values.date.format("YYYY-MM-DD") : "");
    FORM_DATA.append("start_time", values.start_time ? values.start_time.format("HH:mm") : "");
    FORM_DATA.append("end_time", values.end_time ? values.end_time.format("HH:mm") : "");
    FORM_DATA.append("entry_type", values.entry_type);
    FORM_DATA.append("duration", values.duration || "");
    FORM_DATA.append("description", values.description || "");

    try {
      post(ApiURL.view_or_update_rest_hours_st, FORM_DATA).then((res) => {
        if (res?.status === true) {
          SuccessToast("Entry updated successfully");
          navigate("/log-hours");
        } else {
          ErrorToast(res?.message || "Failed to update entry");
        }
        set_loader(false);
      });
    } catch (error) {
      console.error("Error updating entry:", error);
      ErrorToast("An unexpected error occurred");
      set_loader(false);
    }
  };

  return (
    <>
      <VoyageDetails staff_dashboard={staff_dashboard?.voyage_details} buttonShow={false} />

      <div className="card p-3 mt-3">
        <h5 className="mb-3">Edit Rest Hours Entry</h5>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          onValuesChange={onValuesChange}
        >
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item
                label="Date"
                name="date"
                rules={[{ required: true, message: "Required" }]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  format="YYYY-MM-DD"
                  disabledDate={(current) => current && current > dayjs().endOf("day")}
                />
              </Form.Item>
            </Col>

            <Col span={6}>
              <Form.Item
                label="Entry Type"
                name="entry_type"
                rules={[{ required: true, message: "Required" }]}
              >
                <Select placeholder="Select type">
                  {ENTRY_TYPES.map((t) => (
                    <Select.Option key={t.value} value={t.value}>
                      {t.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={4}>
              <Form.Item
                label="Start Time"
                name="start_time"
                rules={[{ required: true, message: "Required" }]}
              >
                <TimePicker format="HH:mm" style={{ width: "100%" }} minuteStep={15} />
              </Form.Item>
            </Col>

            <Col span={4}>
              <Form.Item
                label="End Time"
                name="end_time"
                rules={[{ required: true, message: "Required" }]}
              >
                <TimePicker format="HH:mm" style={{ width: "100%" }} minuteStep={15} />
              </Form.Item>
            </Col>

            <Col span={4}>
              <Form.Item label="Duration (hrs)" name="duration">
                <Input readOnly placeholder="Auto-calculated" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Description" name="description">
                <TextArea rows={3} placeholder="Optional notes" />
              </Form.Item>
            </Col>
          </Row>

          <div style={{ display: "flex", gap: "10px" }}>
            <Button type="primary" htmlType="submit" loading={loader} className="save_btn">
              Update Entry
            </Button>
            <Button onClick={() => navigate("/log-hours")}>Cancel</Button>
          </div>
        </Form>
      </div>
    </>
  );
};

export default EditRestHours;
